import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import Warning from "components/icons/Warning";
import {
    LicenseKeyError,
    MAX_IDENTIFIER_LENGTH,
    MAX_KEY_LENGTH,
} from "components/licenses/bulk-import-license-key/ImportLicenseKeyDialog";
import headerStyle from "components/licenses/view-import-license-keys-view.scss";
import styles from "components/licenses/view-import-license-keys-view.scss";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import StaticTable from "components/support/api-guide/StaticTable";
import TextWithTooltip from "components/table/TextWithTooltip";
import highlightStyle from "components/tenants/edit-tenant/edit-tenant.scss";
import Tooltip from "components/tooltip/Tooltip";
import form from "styles/form.scss";
import { formatDateWithoutTime } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

export interface SerialLicenseKey {
    license_key: string;
    identifier: string;
    assign_count: number;
    start_date: string;
    expiration_date: string;
    license_id: string;
}

interface Props {
    importView?: boolean;
    totalCsvLicenseKeysCount?: number;
    validLicenseKeys: SerialLicenseKey[];
    failedLicenseKeys: SerialLicenseKey[];
    availableLicensesCount: number;
    remainingLicensesCount: number;
    onViewImportClose: () => void;
    licenseKeyCreationStatusDialog: boolean;
    title?: string;
    onCloseButton?: () => void;
    onUploadAgainButton?: () => void;
    onTitleChange?: (title: string) => void;
    licenseKeysWithErrors: Map<string, LicenseKeyError> | undefined;
    oemLicenseExpirationDate?: string;
}

const ViewImportedLicenseKeys = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const isKeyInErrorList = (licenseKey: string) => {
        if (props.licenseKeysWithErrors?.has(licenseKey)) {
            return props.licenseKeysWithErrors?.get(licenseKey);
        } else {
            return "";
        }
    };
    const showTableContent = () => {
        return props.validLicenseKeys.map((each, index) => {
            const errorKey = isKeyInErrorList(each.license_key);
            return [
                <div key={`${index}-license_key`} className={classNames(headerStyle.label, headerStyle.truncate)}>
                    <Tooltip content={each.license_key}>
                        <span>{each.license_key}</span>
                    </Tooltip>
                </div>,
                <div key={`${index}-assign_count`} className={classNames(headerStyle.label)}>
                    {each.assign_count}
                </div>,
                <div key={`${index}-start_date`} className={classNames(headerStyle.label)}>
                    {formatDateWithoutTime(each.start_date)}
                </div>,
                <div key={`${index}-expiration_date`} className={classNames(headerStyle.label)}>
                    {formatDateWithoutTime(each.expiration_date)}
                </div>,
                <div key={`${index}-identifier`} className={headerStyle.label}>
                    {each.identifier}
                </div>,
                <div key={`${index}-status`} className={classNames(headerStyle.label)}>
                    {(() => {
                        if (!errorKey || typeof errorKey !== "object") {
                            return (
                                <div key="success-key" className={headerStyle.successTextColor}>
                                    <StatusBadge
                                        key={"index"}
                                        values={[
                                            {
                                                status: Status.NEUTRAL,
                                                title: "Valid",
                                            },
                                        ]}
                                    />
                                </div>
                            );
                        }
                        const errors = errorKey.failingFields || [];
                        const errorFieldMap = new Set([
                            t("ViewOemKeys.table.licenseKeyExistsErrorMessageHighlight"),
                            t("ViewOemKeys.table.licenseKeyLengthErrorMessageHighlight", { maxLength: MAX_KEY_LENGTH }),
                            t("ViewOemKeys.table.licenseKeyWrongCharactersHighlight"),
                            t("ViewOemKeys.table.licenseKeyWrongIdentifierLengthHighlight", {
                                maxLength: MAX_IDENTIFIER_LENGTH,
                            }),
                            t("ViewOemKeys.table.assignCountMessageHighlight"),
                            t("ViewOemKeys.table.startDateMessageHighlight"),
                            t("ViewOemKeys.table.startDateIsEmptyMessageHighlight"),
                            t("ViewOemKeys.table.startDateFormatErrorMessageHighlight"),
                            t("ViewOemKeys.table.expirationDateErrorMessageHighlight"),
                            t("ViewOemKeys.table.expirationDateIsEmptyMessageHighlight"),
                            t("ViewOemKeys.table.expirationDateFormatErrorMessageHighlight"),
                            t("ViewOemKeys.table.licenseIdIsEmptyOrInvalidMessageHighlight"),
                            t("ViewOemKeys.table.startDateGreaterThanExpirationDateMessageHighlight"),
                        ]);

                        const errorMessages = errors.filter((field) => errorFieldMap.has(field));
                        return errors.length > 0 ? (
                            <div>
                                {errorMessages.map((error) => (
                                    <div key="error-key" className={headerStyle.errorText}>
                                        <StatusBadge
                                            key={"index"}
                                            values={[
                                                {
                                                    status: Status.ERROR,
                                                    title: error,
                                                },
                                            ]}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : undefined;
                    })()}
                </div>,
            ];
        });
    };

    const showFailMessage = (
        <div>
            <span
                className={classNames(headerStyle.introductionLabel)}
                data-testid={testIds.workArea.license.serialNumber.importKeysView.keysSummaryLabel}
            >
                {t("ViewOemKeys.introduction", {
                    licenseExpirationDate: formatDateWithoutTime(props.oemLicenseExpirationDate ?? ""),
                    availableLicensesCount: props.availableLicensesCount,
                    remainingLicensesCount:
                        props.availableLicensesCount - props.remainingLicensesCount < 0
                            ? 0
                            : props.availableLicensesCount - props.remainingLicensesCount,
                })}
            </span>
        </div>
    );
    return (
        <div className={classNames(headerStyle.tableWrapper)}>
            <div className={classNames(form.formFieldsColumnDirection)}>
                <div className={form.formFields}>{showFailMessage}</div>
                <div className={headerStyle.viewStaticTable}>
                    <StaticTable
                        headers={[
                            {
                                className: headerStyle.firstColumnHeader,
                                value: t("ViewOemKeys.table.SerialNumber"),
                            },
                            {
                                className: headerStyle.firstColumnHeader,
                                value:
                                    props.availableLicensesCount - props.remainingLicensesCount > 0 ? (
                                        t("ViewOemKeys.table.assignedLicenses")
                                    ) : (
                                        <>
                                            {t("ViewOemKeys.table.assignedLicenses")}

                                            <TextWithTooltip
                                                text={t("ViewOemKeys.table.assignCountExceedsHighlight", {
                                                    count: props.availableLicensesCount,
                                                })}
                                            >
                                                <span className={headerStyle.leftMargin}>
                                                    <Warning
                                                        color={theme.errorBackgroundColor}
                                                        strokeColor={theme.contentBackgroundColor}
                                                        width={20}
                                                        height={18}
                                                    />
                                                </span>
                                            </TextWithTooltip>
                                        </>
                                    ),
                            },
                            {
                                className: headerStyle.firstColumnHeader,
                                value: t("ViewOemKeys.table.startDate"),
                            },
                            {
                                className: headerStyle.firstColumnHeader,
                                value: t("ViewOemKeys.table.expirationDate"),
                            },
                            {
                                className: headerStyle.firstColumnHeader,
                                value: t("ViewOemKeys.table.identifier"),
                            },
                            {
                                className: headerStyle.firstColumnHeader,
                                value: t("ViewOemKeys.table.errorStatus"),
                            },
                        ]}
                        cells={showTableContent()}
                        testId={testIds.workArea.license.serialNumber.importKeysView.table.itself}
                    />
                </div>
            </div>
            {props.licenseKeysWithErrors && props.licenseKeysWithErrors?.size > 0 ? (
                <div
                    className={classNames(highlightStyle.highlightDiscardedLicenseType, headerStyle.topMargin)}
                    data-testid={testIds.common.subpageView.errorBanner}
                >
                    <label className={classNames(styles.warning)}>
                        <div className={classNames(styles.warningIconWrapper)}>
                            <Warning
                                color={theme.errorBackgroundColor}
                                strokeColor={theme.contentBackgroundColor}
                                width={20}
                                height={48}
                            />
                        </div>
                        {t("ImportOemSerialLicenseKeys.importOemSerialLicenseKeys.failureErrorStripMessage")}
                    </label>
                </div>
            ) : null}
        </div>
    );
};

export default ViewImportedLicenseKeys;
